/**
 * 교시 종소리 페이지 — 2026-06-30
 * 독서실 전용 PC 브라우저에서 /bell/{token} 으로 열어둠. 공개(로그인 불필요).
 * 매초 KST 시각·요일을 종 스케줄과 대조해 해당 시각이면 mp3 재생.
 * 브라우저 자동재생 정책 때문에 첫 진입 시 "소리 켜기" 1회 클릭 필요.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

interface Entry { ring_time: string; weekdays: number[]; sound: string; label: string | null; }
interface Board { organization: { name: string }; is_enabled: boolean; volume: number; schedule: Entry[]; }

const WD = ['일', '월', '화', '수', '목', '금', '토'];
const SOUND_FILES: Record<string, string> = {
  bell1: '/bell-sounds/bell1.mp3', bell2: '/bell-sounds/bell2.mp3', bell3: '/bell-sounds/bell3.mp3',
  music1: '/bell-sounds/music1.mp3', music2: '/bell-sounds/music2.mp3',
};
const SOUND_LABEL: Record<string, string> = {
  bell1: '시작종', bell2: '끝종', bell3: '예비종', music1: '음악1', music2: '음악2',
};

function kstNow() {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
  const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const dateStr = d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });
  const timeStr = d.toLocaleTimeString('ko-KR');
  return { hhmm, ymd, weekday: d.getDay(), ss: d.getSeconds(), dateStr, timeStr };
}

export default function BellPage() {
  const { token } = useParams<{ token: string }>();
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState('');
  const [armed, setArmed] = useState(false);     // 소리 켜짐(사용자 제스처 후)
  const [clock, setClock] = useState<{ date: string; time: string }>({ date: '', time: '' });
  const [lastRung, setLastRung] = useState<string>('');
  const audiosRef = useRef<Record<string, HTMLAudioElement>>({});
  const rungRef = useRef<Set<string>>(new Set());
  const boardRef = useRef<Board | null>(null);

  // 오디오 엘리먼트 준비
  useEffect(() => {
    Object.entries(SOUND_FILES).forEach(([k, src]) => {
      const a = new Audio(src);
      a.preload = 'auto';
      audiosRef.current[k] = a;
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/public/bell/board?token=${encodeURIComponent(token || '')}`);
      if (!res.ok) { setError('유효하지 않은 종소리 주소입니다'); return; }
      setError('');
      const b = await res.json();
      setBoard(b);
      boardRef.current = b;
    } catch {
      setError('연결 오류 — 잠시 후 다시 시도합니다');
    }
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 300000); // 5분마다 스케줄 갱신
    return () => clearInterval(t);
  }, [load]);

  const [armMsg, setArmMsg] = useState('');
  const arm = () => {
    setArmed(true);
    const vol = Math.max(0, Math.min(1, (boardRef.current?.volume ?? 100) / 100));
    let ok = false;
    // 확인음(bell3) 1회 재생 + 나머지 오디오 잠금 해제 (사용자 제스처 안에서)
    Object.entries(audiosRef.current).forEach(([k, a]) => {
      a.volume = vol;
      if (k === 'bell3') {
        a.currentTime = 0;
        a.play().then(() => { ok = true; setArmMsg('✅ 소리 정상 — 방금 확인음이 들렸나요?'); })
          .catch((e) => setArmMsg(`⚠ 소리 재생 차단됨: ${e?.name || '오류'} (브라우저/시스템 음소거 확인)`));
      } else {
        a.play().then(() => { a.pause(); a.currentTime = 0; }).catch(() => {});
      }
    });
    setTimeout(() => { if (!ok) setArmMsg((m) => m || '⚠ 확인음이 안 들리면: 탭 음소거·시스템 볼륨·브라우저 사이트 소리권한 확인'); }, 1500);
  };

  const playSound = useCallback((sound: string, volume: number) => {
    const a = audiosRef.current[sound] || audiosRef.current['bell1'];
    if (!a) return;
    a.volume = Math.max(0, Math.min(1, volume / 100));
    a.currentTime = 0;
    a.play().catch(() => {});
  }, []);

  // 매초 시각 체크 → 종 울리기
  useEffect(() => {
    const t = setInterval(() => {
      const { hhmm, ymd, weekday, dateStr, timeStr } = kstNow();
      setClock({ date: dateStr, time: timeStr });
      const b = boardRef.current;
      if (!armed || !b || !b.is_enabled) return;
      b.schedule.forEach((e, i) => {
        if (e.ring_time === hhmm && (e.weekdays || []).includes(weekday)) {
          const key = `${ymd}_${e.ring_time}_${e.sound}_${i}`;
          if (!rungRef.current.has(key)) {
            rungRef.current.add(key);
            playSound(e.sound, b.volume);
            setLastRung(`${e.ring_time} ${e.label || SOUND_LABEL[e.sound] || ''} 🔔`);
          }
        }
      });
      // 자정 지나면 rung 기록 정리(메모리)
      if (hhmm === '00:01' && rungRef.current.size > 0) rungRef.current.clear();
    }, 1000);
    return () => clearInterval(t);
  }, [armed, playSound]);

  const center: React.CSSProperties = { minHeight: '100vh', background: '#0b1220', color: '#e5e7eb', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'Pretendard, system-ui, sans-serif', padding: 24, boxSizing: 'border-box' };

  if (error && !board) return <div style={{ ...center, color: '#f87171', fontSize: 24 }}>{error}</div>;
  if (!board) return <div style={{ ...center, color: '#9ca3af', fontSize: 24 }}>불러오는 중...</div>;

  const upcoming = [...board.schedule].sort((a, b) => a.ring_time.localeCompare(b.ring_time)).find((e) => e.ring_time >= kstNow().hhmm);

  return (
    <div style={center}>
      <div style={{ fontSize: 22, color: '#9ca3af', marginBottom: 12 }}>🔔 {board.organization.name} 교시 종소리</div>
      {/* 날짜 / 시간 두 줄 */}
      <div style={{ fontSize: 'clamp(20px, 4vw, 36px)', fontWeight: 700, color: '#cbd5e1' }}>{clock.date || kstNow().dateStr}</div>
      <div style={{ fontSize: 'clamp(48px, 13vw, 120px)', fontWeight: 900, letterSpacing: '0.02em', lineHeight: 1.05 }}>{clock.time || kstNow().timeStr}</div>

      {/* 소리 켜기/끄기 토글 */}
      <button
        onClick={() => (armed ? setArmed(false) : arm())}
        style={{ marginTop: 24, padding: '16px 32px', fontSize: 22, fontWeight: 800, borderRadius: 16, border: 'none', cursor: 'pointer',
          background: armed ? '#ef4444' : '#22c55e', color: armed ? '#fff' : '#04210f' }}
      >
        {armed ? '🔕 소리 끄기' : '🔔 소리 켜기 (한 번만 누르면 됩니다)'}
      </button>
      <div style={{ marginTop: 14, fontSize: 17, color: !armed ? '#9ca3af' : (board.is_enabled ? '#34d399' : '#f59e0b') }}>
        {!armed ? '소리가 꺼져 있습니다 — 위 버튼을 누르세요'
          : (board.is_enabled ? '● 종소리 작동 중' : '⚠ 종소리 기능 OFF — 운영자 설정(시설 정보)에서 켜세요')}
      </div>
      {armMsg && <div style={{ marginTop: 8, fontSize: 14, color: armMsg.startsWith('✅') ? '#34d399' : '#fbbf24' }}>{armMsg}</div>}

      <div style={{ marginTop: 28, textAlign: 'center', color: '#cbd5e1', fontSize: 16 }}>
        {upcoming ? (
          <>다음 종: <b style={{ color: '#fff' }}>{upcoming.ring_time}</b> {upcoming.label || SOUND_LABEL[upcoming.sound] || ''}</>
        ) : '오늘 남은 종 없음'}
        {lastRung && <div style={{ marginTop: 6, color: '#fbbf24' }}>방금: {lastRung}</div>}
      </div>

      <div style={{ marginTop: 28, maxWidth: 520, width: '100%' }}>
        <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 6 }}>등록된 종 ({board.schedule.length}) · 추가/수정은 운영자 설정(시설 정보 → 교시 종소리)에서</div>
        <div style={{ maxHeight: 220, overflowY: 'auto', background: '#111827', borderRadius: 12, padding: 8 }}>
          {board.schedule.length === 0 ? (
            <div style={{ color: '#6b7280', padding: 10 }}>설정에서 종을 추가하세요</div>
          ) : (
            [...board.schedule].sort((a, b) => a.ring_time.localeCompare(b.ring_time)).map((e, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderBottom: '1px solid #1f2937', fontSize: 15 }}>
                <span style={{ width: 56, fontWeight: 700, color: '#fff' }}>{e.ring_time}</span>
                <span style={{ flex: 1, color: '#cbd5e1' }}>{e.label || ''}</span>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>{SOUND_LABEL[e.sound] || e.sound} · {(e.weekdays || []).map((w) => WD[w]).join('')}</span>
              </div>
            ))
          )}
        </div>
      </div>
      <div style={{ marginTop: 18, color: '#4b5563', fontSize: 12, textAlign: 'center' }}>
        이 화면을 켜두세요. PC 절전(잠자기)을 끄면 계속 울립니다.
      </div>
    </div>
  );
}
